// The design system, DERIVED from tokens.json (SPEC §15).
//
// "The token set must be exported as framework-neutral JSON that both the Tamagui
// config and the Go generator read, with the Tamagui config *derived* from that
// file rather than being the source of truth. Otherwise the cards drift out of
// the interface's palette one theme tweak at a time, and the card is the one
// artefact strangers see first."
//
// So: not one hex literal in this file. Every colour here is either a value read
// out of tokens.json or that value with an alpha applied. The rule is mechanical
// and it is checked by a test, because "derived from the token file" is the kind
// of claim that stays true for exactly as long as nobody is in a hurry.
//
// This module is PURE — no createTamagui call, no CSS injection, no DOM. That is
// what lets the derivation be tested in node, and it is also what lets the same
// objects be dumped to JSON for anything else that needs the palette.

import tokens from '../tokens.json';

export type Hex = string;

/** The raw token file, re-exported so callers never reach for a literal. */
export const raw = tokens;

/**
 * `#rrggbb` plus an alpha, as an rgba() string.
 *
 * Translucency is not decoration here: §15 requires panels to be "translucent and
 * blurred over the map, never opaque — the war stays visible behind the UI at all
 * times". An opaque panel is a spec violation, not a style choice.
 */
export function withAlpha(hex: Hex, alpha: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/** Mixes two token colours. Used only to derive rules and rails, never to invent a hue. */
export function mix(a: Hex, b: Hex, t: number): Hex {
  const pa = parseInt(a.replace('#', ''), 16);
  const pb = parseInt(b.replace('#', ''), 16);
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ch = (shift: number): number => {
    const x = (pa >> shift) & 255;
    const y = (pb >> shift) & 255;
    return Math.round(x + (y - x) * k);
  };
  const hex = (v: number): string => v.toString(16).padStart(2, '0');
  return `#${hex(ch(16))}${hex(ch(8))}${hex(ch(0))}`;
}

// --- colour ---------------------------------------------------------------

/**
 * The interface palette.
 *
 * Nine named colours out of the file, plus the translucent and mixed variants the
 * chrome needs. Country flag colours are deliberately absent: they live in
 * render/palette.ts at FULL saturation because they are the content, and this
 * palette is drained on purpose so that they carry.
 */
export const colour = {
  base: tokens.color.base,
  panel: tokens.color.panel,
  line: tokens.color.line,
  cyan: tokens.color.cyan,
  phosphor: tokens.color.phosphor,
  amber: tokens.color.amber,
  alert: tokens.color.alert,
  text: tokens.color.text,
  textDim: tokens.color.textDim,

  /** Panels: translucent over the canvas, never opaque. */
  panelFill: withAlpha(tokens.color.panel, 0.72),
  panelFillDeep: withAlpha(tokens.color.base, 0.82),
  /** Hairline rules and the tick-marked rails. */
  hairline: withAlpha(tokens.color.line, 0.85),
  hairlineFaint: withAlpha(tokens.color.line, 0.45),
  bracket: withAlpha(tokens.color.cyan, 0.55),
  /** A wash for a selected row: the interface colour at almost nothing. */
  wash: withAlpha(tokens.color.cyan, 0.08),
  scanline: withAlpha(tokens.color.cyan, 0.05),
  shadow: withAlpha(tokens.color.base, 0.6),
  transparent: 'rgba(0, 0, 0, 0)',
} as const;

/**
 * The three click outcomes, and the three that are not failures.
 *
 * Same six colours, in the same order, as render/overlay.ts's OVERLAY_COLOURS.
 * SPEC §3 requires the failures to look different from each other; §15 requires
 * the HUD and the map to agree about which is which, and the only way to be sure
 * of that is for both to read the same token names.
 */
export const outcomeColour = {
  pending: colour.cyan,
  applied: colour.phosphor,
  captured: colour.phosphor,
  illegal: colour.amber,
  lost: colour.alert,
  dropped: colour.textDim,
} as const;

export type OutcomeColourName = keyof typeof outcomeColour;

// --- type -----------------------------------------------------------------

export const fontFamily = {
  /** Monospace throughout the interface. */
  mono: tokens.font.mono,
  /** Clean sans for anything a person wrote. Never the theme font. */
  sans: tokens.font.sans,
} as const;

/**
 * The type scale.
 *
 * Small and tight: this is an instrument panel, and the sizes above 7 exist for
 * the one or two numbers the whole screen is about (the bank, the season clock).
 */
export const fontSize = {
  1: 10,
  2: 11,
  3: 12,
  4: 13,
  5: 15,
  6: 18,
  7: 22,
  8: 28,
  true: 13,
} as const;

export const lineHeight = {
  1: 14,
  2: 15,
  3: 16,
  4: 18,
  5: 20,
  6: 24,
  7: 28,
  8: 34,
  true: 18,
} as const;

/** Letter spacing widens as size drops, which is what keeps 10px mono legible. */
export const letterSpacing = {
  1: 1.1,
  2: 0.9,
  3: 0.7,
  4: 0.5,
  5: 0.3,
  6: 0.1,
  7: 0,
  8: -0.2,
  true: 0.5,
} as const;

// --- space, size, radius --------------------------------------------------

export const space = {
  hair: tokens.space.hair,
  xs: tokens.space.xs,
  sm: tokens.space.sm,
  md: tokens.space.md,
  lg: tokens.space.lg,
  xl: tokens.space.xl,
  true: tokens.space.sm,
} as const;

export const size = {
  hair: tokens.space.hair,
  xs: tokens.space.xs,
  sm: tokens.space.sm,
  md: tokens.space.md,
  lg: tokens.space.lg,
  xl: tokens.space.xl,
  true: tokens.space.md,
} as const;

/**
 * Radii are zero on purpose.
 *
 * §15's chrome is "nested rectangles with corner brackets rather than filled
 * panels". A rounded corner is the single fastest way to make this read as a
 * consumer app instead of as instrumentation, so the scale exists — Tamagui wants
 * it — and every step of it is square.
 */
export const radius = {
  0: 0,
  1: 0,
  2: 0,
  true: 0,
} as const;

export const zIndex = {
  map: 0,
  arcs: 10,
  hud: 100,
  sheet: 200,
  modal: 300,
  true: 100,
} as const;

// --- motion ---------------------------------------------------------------

/**
 * Durations, straight out of the token file.
 *
 * §15: "Motion is mechanical and linear. No bounce, no elastic easing." The
 * easing is in the token file as a string for exactly this reason — so that a
 * future spring cannot be introduced by a component author without editing the
 * file the Go share card also reads.
 */
export const motion = {
  scanMs: tokens.motion.scanMs,
  flickerMs: tokens.motion.flickerMs,
  tracerMs: tokens.motion.tracerMs,
  arcFadeMs: tokens.motion.arcFadeMs,
  easing: tokens.motion.easing,
  /** Panel opens and value settles. Short, and linear like everything else. */
  uiMs: 120,
} as const;

// --- the Tamagui inputs ---------------------------------------------------

/**
 * The object handed to `createTokens`.
 *
 * Kept separate from the `createTamagui` call so the derivation is a plain value
 * a test can walk, and so nothing in the pure half of the UI has to import the
 * component library to know what colour something is.
 */
export const tamaguiTokens = {
  color: { ...colour, ...outcomeColourTokens() },
  space,
  size,
  radius,
  zIndex,
} as const;

function outcomeColourTokens(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(outcomeColour)) out[`outcome${k[0]!.toUpperCase()}${k.slice(1)}`] = v;
  return out;
}

/**
 * One theme, and it is dark.
 *
 * §15: "Dense, technical, restrained, and mostly dark. The map is the only bright
 * thing on screen." A light theme is not a missing feature here — it is a
 * different product, and shipping a stub of one would put a second palette in the
 * repo for the share card to drift away from.
 */
export const darkTheme = {
  background: colour.base,
  backgroundHover: colour.wash,
  backgroundPress: colour.wash,
  backgroundFocus: colour.wash,
  backgroundStrong: colour.panel,
  backgroundTransparent: colour.transparent,

  color: colour.text,
  colorHover: colour.cyan,
  colorPress: colour.cyan,
  colorFocus: colour.cyan,
  colorTransparent: colour.transparent,

  borderColor: colour.hairline,
  borderColorHover: colour.cyan,
  borderColorPress: colour.cyan,
  borderColorFocus: colour.cyan,

  placeholderColor: colour.textDim,
  outlineColor: colour.bracket,
  shadowColor: colour.shadow,
  shadowColorHover: colour.shadow,
  shadowColorPress: colour.shadow,
  shadowColorFocus: colour.shadow,
} as const;

export const themes = { dark: darkTheme } as const;

/** The font descriptors, in `createFont` shape. */
export const monoFont = {
  family: fontFamily.mono,
  size: fontSize,
  lineHeight,
  letterSpacing,
  weight: { 1: '400', 4: '400', 6: '500', true: '400' },
} as const;

export const sansFont = {
  family: fontFamily.sans,
  size: fontSize,
  lineHeight,
  letterSpacing: { 1: 0, 4: 0, 8: 0, true: 0 },
  weight: { 1: '400', 4: '400', 6: '600', true: '400' },
} as const;

/**
 * Every distinct colour value the interface can produce, for the derivation test.
 *
 * A colour that is not in here, or not traceable to tokens.json, is a colour that
 * will not be in the share card.
 */
export function paletteValues(): string[] {
  return Object.values(tamaguiTokens.color);
}

/** The token-file hexes, lower-cased. The set every derived colour must come from. */
export function sourceHexes(): string[] {
  return Object.values(tokens.color).map((c) => c.toLowerCase());
}
